import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import * as vega from 'vega';
import * as vegaLite from 'vega-lite';
import * as vl from 'vega-lite-api';
import { Handler } from 'vega-tooltip';
// import { json } from "d3";

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')

const brush = vl.selectInterval().encodings('x');
// const click = vl.selectMulti().encodings('color');
const click2 = vl.selectMulti().encodings('y');

const width = 700;
const width2 = width/2 - 40;
const height = 250;

const colorScale = {
    domain: ['a passes', 'b fails', 'c does not apply'],
    range: ['#00FF00', '#e83838', '#bbb']
}

const config = {
    axis: {
        domain: false,
        tickColor: 'lightGray',
    },
    style: {
        'guide-label': {
            fontSize: 12,
            fill: '#000'
        },
        'guide-title': {
            fontSize: 18,
            fill: '#000'
        }
    },
    legend: {
        disable: true
    }
};

const pointSize = 150;

// registration
vl.register(vega, vegaLite, {
    view: { renderer: 'svg' },
    init: view => { view.tooltip(new Handler().call); }
});

const GraphApiMeasureReportsInteractive = ({ data, graphParams, onIncidentSelect }) => {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const [vegaSpec, setVegaSpec] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [marks, setMarks] = useState(null);
    const [vegaView, setVegaView] = useState(null);

    // Helper function to convert date string to local time for Vega ingestion
    const datetimeConversion = (dateString) => {
        // if datestring is undefined or null, return null
        if (dateString === undefined || dateString === null) {
            return null;
        }

        // convert the date string to a valid ISO 8601 date string and add Z to the end to indicate UTC time
        let isoString = dateString.replace(' ', 'T');
        isoString = isoString + 'Z';

        // now convert the ISO string to a date object
        const date = new Date(isoString);

        // Format the date as YYYY-MM-DD HH:MM:SS
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    
        return formattedDate;
    };

    // Memoize the data processing to prevent unnecessary recalculations
    const processedData = useMemo(() => {
        return data.map(datum => ({
            ...datum,
            unit_back_in_service: datetimeConversion(datum.unit_back_in_service),
            original_unit_back_in_service: datum.unit_back_in_service // Keep original for ReportListElement
        }));
    }, [data]);

    // Filter data and process results in useEffect
    useEffect(() => {
        const filteredData = processedData.reduce((filtered, datum) => {
            if (datum.unit_back_in_service >= '2025-05-01') { // May 1 2025 is when XMLs started being saved
                let result = datum.result;
                if (result === 1) {
                    datum.applies_string = 'a passes';
                } else if (result === 0) {
                    datum.applies_string = 'b fails';
                } else {
                    datum.applies_string = 'c does not apply';
                }
                
                // Use medic_name when available, otherwise fall back to crew_member_id
                datum.display_medic = datum.medic_name || datum.crew_member_id;
                
                filtered.push(datum);
            }
            return filtered;
        }, []);
        setFilteredData(filteredData);
    }, [processedData]);



    // -------------------------------------------------------------------------------------

    const reportPlot = vl.markPoint({
        filled: true,
        stroke: false,
        size: pointSize
    })
        .data(filteredData)
        .encode(
            vl.color().value('lightgray')
                .if(brush, vl.color().fieldN('applies_string').scale(colorScale).title('Result')),
            vl.opacity().value(0.08)
                .if(click2, vl.opacity().value(0.7)),
            vl.order().fieldN('applies_string').sort('descending'),
            vl.x().fieldT('unit_back_in_service')
                .title('Time Span')
                .timeUnit({ unit: 'monthdateyear', utc: false })
                .scale({padding: pointSize/40}),
            vl.y().fieldT('unit_back_in_service')
                .timeUnit({ unit: 'hoursminutes', utc: false })
                .scale({padding: pointSize/40})
                .axis({title: 'Unit Back in Service', format: '%I %p'}),
            vl.tooltip().fieldN('incident_number')
        )
        .width(width)
        .height(height)
        .select(brush)
        // .transform(vl.filter(click))
        // .transform(vl.filter(click2));

    // Create group compliance time series (black line)
    const groupComplianceTimeSeriesPlot = vl.markLine({
        point: { filled: true, size: pointSize }
    })
        .data(filteredData)
        .transform(
            {
                timeUnit: 'monthyear',
                field: 'unit_back_in_service',
                as: 'month_creation'
            },
            {
                aggregate: [
                    {
                        op: 'mean',
                        field: 'result',
                        as: 'mean_result'
                    }
                ],
                groupby: ['month_creation']
            }
        )
        .encode(
            vl.color().value('black'),
            vl.x().fieldT('month_creation')
                .axis({title: null})
                .timeUnit('monthyear'),
            vl.y().fieldQ('mean_result')
                .title('Percent Compliance')
                .scale({domain: [0, 1]})
                .axis({format: '.0%', title: 'Percent Compliance'}),
            vl.tooltip().fieldN('mean_result')
                .format('.2%')
        )
        .width(width)
        .height(height*0.7);

    // Create medic compliance time series (colored line, based on selected medic)
    const medicComplianceTimeSeriesPlot = vl.markLine({
        point: { filled: true, size: pointSize/2 },
        strokeWidth: 2
    })
        .data(filteredData)
        .transform(
            // Filter by selected medic from click2
            vl.filter(click2),
            {
                timeUnit: 'monthyear',
                field: 'unit_back_in_service',
                as: 'month_creation'
            },
            {
                aggregate: [
                    {
                        op: 'mean',
                        field: 'result',
                        as: 'mean_result'
                    }
                ],
                groupby: ['month_creation']
            }
        )
        .encode(
            vl.color().value('#0C7FFA'),
            vl.x().fieldT('month_creation')
                .axis({title: null})
                .timeUnit('monthyear'),
            vl.y().fieldQ('mean_result')
                .title('Percent Compliance')
                .scale({domain: [0, 1]})
                .axis({format: '.0%', title: 'Percent Compliance'}),
            vl.tooltip().fieldN('mean_result')
                .format('.2%')
        )
        .width(width)
        .height(height*0.7);

    // Layer the plots together
    const timeSeriesPlot = vl.layer(medicComplianceTimeSeriesPlot, groupComplianceTimeSeriesPlot);

    const barPlot = vl.markBar()
        .data(filteredData)
        .encode(
            vl.color().fieldN('applies_string').scale(colorScale).title('Result'),
            // vl.color().value('lightgray')
                // .if(click, vl.color().fieldN('applies_string').scale(colorScale).title('Result')),
            vl.x().count()
                .axis({title: 'Number of Reports'}),
            vl.y().fieldN('applies_string')
                .title('Result')
                .scale({domain: colorScale.domain})
        )
        .width(width)
        // .select(click)
        .transform(vl.filter(brush));

    const topMedicsPlot = vl.markBar()
        .data(filteredData)
        .transform(
            // Apply brush filter first to filter by time range
            vl.filter(brush),
            // Filter only fails
            vl.filter('datum.applies_string === "b fails"'),
            // Aggregate by display_medic to count fails
            {
                aggregate: [
                    {
                        op: 'count',
                        as: 'fail_count'
                    }
                ],
                groupby: ['display_medic']
            },
            // Sort by fail count descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'fail_count',
                        order: 'descending'
                    }
                ]
            },
            // Filter to only show top 5
            vl.filter('datum.rank <= 5')
        )
        .encode(
            vl.x().fieldQ('fail_count')
                .title('Number of Fails'),
            vl.y().fieldN('display_medic')
                .title('Medic')
                .sort('-x'),
            vl.color().value('lightgray')
                .if(click2, vl.color().value('#e83838')),
            vl.tooltip().fieldN('fail_count')
                .title('Total Fails')
        )
        .width(width2)
        .height(height)
        .title('Top Medics by Fail Count')
        .select(click2);

    const failRatePlot = vl.markBar()
        .data(filteredData)
        .transform(
            // Apply brush filter first to filter by time range
            vl.filter(brush),
            // Aggregate by display_medic to count passes and fails separately
            {
                aggregate: [
                    {
                        op: 'count',
                        as: 'count'
                    }
                ],
                groupby: ['display_medic', 'applies_string']
            },
            // Pivot to get passes and fails as separate columns
            {
                pivot: 'applies_string',
                value: 'count',
                groupby: ['display_medic']
            },
            // Calculate fail rate: fails / (passes + fails)
            {
                calculate: '(datum["b fails"] || 0) / ((datum["a passes"] || 0) + (datum["b fails"] || 0))',
                as: 'fail_rate_raw'
            },
            // Flag for all fails (no passes)
            {
                calculate: '(!datum["a passes"] || datum["a passes"] === 0) && (datum["b fails"] && datum["b fails"] > 0) ? 1 : 0',
                as: 'all_fails_flag'
            },
            // Use max of calculated rate or all-fails flag
            {
                calculate: 'max(datum.fail_rate_raw, datum.all_fails_flag)',
                as: 'fail_rate'
            },
            // Sort by fail rate descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'fail_rate',
                        order: 'descending'
                    }
                ]
            },
            // Filter out medics with 0% fail rate
            vl.filter('datum.fail_rate > 0'),
            // Sort by fail rate descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'fail_rate',
                        order: 'descending'
                    }
                ]
            },
            // Filter to only show top 10
            vl.filter('datum.rank <= 10')
        )
        .encode(
            vl.x().fieldQ('fail_rate')
                .title('Fail Rate')
                .scale({domain: [0, 1]})
                .axis({format: '.0%'}),
            vl.y().fieldN('display_medic')
                .title(null)
                .sort('-x'),
            vl.color().value('lightgray')
                .if(click2, vl.color().value('#e83838')),
            vl.tooltip().fieldN('fail_rate')
                .title('Fail Rate')
                .format('.1%')
        )
        .width(width2)
        .height(height)
        .title('Top Medics by Fail Rate')
        .select(click2);

    const topSuccessMedicsPlot = vl.markBar()
        .data(filteredData)
        .transform(
            // Apply brush filter first to filter by time range
            vl.filter(brush),
            // Filter only passes
            vl.filter('datum.applies_string === "a passes"'),
            // Aggregate by display_medic to count passes
            {
                aggregate: [
                    {
                        op: 'count',
                        as: 'success_count'
                    }
                ],
                groupby: ['display_medic']
            },
            // Sort by success count descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'success_count',
                        order: 'descending'
                    }
                ]
            },
            // Filter to only show top 5
            vl.filter('datum.rank <= 5')
        )
        .encode(
            vl.x().fieldQ('success_count')
                .title('Number of Passes'),
            vl.y().fieldN('display_medic')
                .title(null)
                .sort('-x'),
            vl.color().value('lightgray')
                .if(click2, vl.color().value('#00FF00')),
            vl.tooltip().fieldN('success_count')
                .title('Total Passes')
        )
        .width(width2)
        .height(height)
        .title('Top Medics by Pass Count')
        .select(click2);

    const successRatePlot = vl.markBar()
        .data(filteredData)
        .transform(
            // Apply brush filter first to filter by time range
            vl.filter(brush),
            // Aggregate by display_medic to count passes and fails separately
            {
                aggregate: [
                    {
                        op: 'count',
                        as: 'count'
                    }
                ],
                groupby: ['display_medic', 'applies_string']
            },
            // Pivot to get passes and fails as separate columns
            {
                pivot: 'applies_string',
                value: 'count',
                groupby: ['display_medic']
            },
            // Calculate success rate: passes / (passes + fails)
            {
                calculate: 'datum["a passes"] / (datum["a passes"] + (datum["b fails"] || 0))',
                as: 'success_rate_raw'
            },
            // Flag for all passes (no fails)
            {
                calculate: '(!datum["b fails"] || datum["b fails"] === 0) && datum["a passes"] > 0 ? 1 : 0',
                as: 'all_passes_flag'
            },
            // Use max of calculated rate or all-passes flag
            {
                calculate: 'max(datum.success_rate_raw, datum.all_passes_flag)',
                as: 'success_rate'
            },
            // Sort by success rate descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'success_rate',
                        order: 'descending'
                    }
                ]
            },
            // Filter out medics with 0% success rate
            vl.filter('datum.success_rate > 0'),
            // Sort by success rate descending and rank
            {
                window: [
                    {
                        op: 'rank',
                        as: 'rank'
                    }
                ],
                sort: [
                    {
                        field: 'success_rate',
                        order: 'descending'
                    }
                ]
            },
            // Filter to only show top 10
            vl.filter('datum.rank <= 10')
        )
        .encode(
            vl.x().fieldQ('success_rate')
                .title('Pass Rate')
                .scale({domain: [0, 1]})
                .axis({format: '.0%'}),
            vl.y().fieldN('display_medic')
                .title(null)
                .sort('-x'),
            vl.color().value('lightgray')
                .if(click2, vl.color().value('#00FF00')),
            vl.tooltip().fieldN('success_rate')
                .title('Pass Rate')
                .format('.1%')
        )
        .width(width2)
        .height(height)
        .title('Top Medics by Pass Rate')
        .select(click2);

    // -------------------------------------------------------------------------------------

    // Create medic summary table data
    const [medicTableData, setMedicTableData] = useState([]);

    useEffect(() => {
        // Process data to create medic summary
        const medicSummary = {};
        
        filteredData.forEach(datum => {
            const medicId = datum.display_medic;
            if (!medicSummary[medicId]) {
                medicSummary[medicId] = {
                    display_medic: medicId,
                    pass_count: 0,
                    fail_count: 0,
                    total_count: 0
                };
            }
            
            if (datum.applies_string === 'a passes') {
                medicSummary[medicId].pass_count++;
            } else if (datum.applies_string === 'b fails') {
                medicSummary[medicId].fail_count++;
            }
            medicSummary[medicId].total_count++;
        });

        // Calculate rates and convert to array
        const tableData = Object.values(medicSummary).map(medic => {
            const pass_rate = medic.total_count > 0 ? (medic.pass_count / medic.total_count) : 0;
            const fail_rate = medic.total_count > 0 ? (medic.fail_count / medic.total_count) : 0;
            
            return {
                ...medic,
                pass_rate: pass_rate,
                fail_rate: fail_rate,
                pass_rate_pct: (pass_rate * 100).toFixed(1),
                fail_rate_pct: (fail_rate * 100).toFixed(1)
            };
        }).sort((a, b) => b.total_count - a.total_count); // Sort by total count descending

        setMedicTableData(tableData);
    }, [filteredData]);

    useEffect(() => {
        const failurePlots = vl.hconcat(topMedicsPlot, failRatePlot);
        const successPlots = vl.hconcat(topSuccessMedicsPlot, successRatePlot);
        const medicPlots = vl.vconcat(failurePlots, successPlots);
        const vegaLiteSpec = vl.vconcat(timeSeriesPlot, reportPlot, medicPlots)
            .config(config)
            .toSpec();
        setVegaSpec(vegaLite.compile(vegaLiteSpec).spec);
    }, [filteredData]);

    useEffect(() => {
        if (vegaSpec) {
            const runtime = vega.parse(vegaSpec);
            const view = new vega.View(runtime)
                .renderer("svg")
                .initialize(containerRef.current)
                .hover()
                .tooltip(new Handler().call);

            view.addEventListener("click", (event, item) => {
                console.log("Point clicked");
                if (item.datum.incident_number !== undefined && item.datum.incident_number !== null) {
                    onIncidentSelect(item.datum.incident_number);
                }
            });

            view.run();

            return () => {
                view.finalize();
            };
        }
    }, [vegaSpec]);

    useEffect(() => {
        if (marks) {
            marks.render().then(view => {
                setVegaView(view);
                console.log('Vega view set again');
            }).catch(error => {
                console.error('Error rendering Vega view:', error);
            });
        }
    }, [marks]);


    return (
        <div>
            <div ref={containerRef} />
            
            {/* Medic Summary Table */}
            {/* <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '15px', color: '#333' }}>Medic Performance Summary</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse', 
                        fontSize: '14px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Medic</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Total Reports</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', color: '#28a745' }}>Passes</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', color: '#dc3545' }}>Fails</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', color: '#28a745' }}>Pass Rate</th>
                                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', color: '#dc3545' }}>Fail Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {medicTableData.map((medic, index) => (
                                <tr key={medic.display_medic} style={{ 
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                    borderBottom: '1px solid #dee2e6'
                                }}>
                                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{medic.display_medic}</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>{medic.total_count}</td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#28a745' }}>{medic.pass_count}</td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545' }}>{medic.fail_count}</td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#28a745' }}>{medic.pass_rate_pct}%</td>
                                    <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545' }}>{medic.fail_rate_pct}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div> */}
        </div>
    );
};

export default GraphApiMeasureReportsInteractive;